import React from 'react';
import PropTypes from 'prop-types';

import { findClosestAncestor } from '../utils/element';

const KEYCODES = {
  TAB:   9,
  ENTER: 13,
};

/*
 * Tabbed Browsing component.
 *
 * Summary of responsibilities:
 *
 *  - listens for key events to handle focus change. This allows for distinguishing b/t key(tab), and click focus
 *  - handles adding/removing 'hasFocus' class on document's activeElement when activeElement is within specified container.
 *  - handles adding/removing 'focusWithin' class on element with specified focusableClass when activeElement is within.
 *  - calls on enter key handler when specified
 *  - listens for document click event to remove 'hasFocus' and 'hasFocusWithin' class
 */

class TabbedBrowsing extends React.Component {
  constructor() {
    super();
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.addFocusClass = this.addFocusClass.bind(this);
    this.removeFocusClass = this.removeFocusClass.bind(this);
    this.removeFocusWithinClass = this.removeFocusWithinClass.bind(this);
  }

  componentDidMount() {
    // listen for click events from parent when embedded
    if (NGX.App.state.framed) {
      const eventService = NGXD.getServiceContainer().getService('event');
      eventService.listen('parent:event:click', this.handleClick);
    }

    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
    document.addEventListener('click', this.handleClick);
  }

  componentWillUnmount() {
    const eventService = NGXD.getServiceContainer().getService('event');
    eventService.unlisten('parent:event:click', this.handleClick);

    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
    document.removeEventListener('click', this.handleClick);
  }

  handleKeyDown({ keyCode, shiftKey }) {
    switch (keyCode) {
      case KEYCODES.TAB:
        // if tabbing backwards off of a focusable element, we want to remove the 'focusWithin' class from that element
        const itemWillLooseFocus = shiftKey && document.activeElement.classList.contains(this.props.focusableClass);
        itemWillLooseFocus && this.removeFocusWithinClass();

        this.removeFocusClass();
        break;
      case KEYCODES.ENTER:
        !!this.props.onEnter && this.props.onEnter(document.activeElement);
        break;
    }
  }

  handleKeyUp({ keyCode }) {
    if (keyCode === KEYCODES.TAB) {
      this.addFocusClass();
      !!this.props.onTab && this.props.onTab(document.activeElement);
    }
  }

  handleClick() {
    this.removeFocusClass();
    this.removeFocusWithinClass();
    !!this.props.onClick && this.props.onClick(document.activeElement);
  }

  removeFocusClass() {
    const container = document.querySelector(this.props.containerSelector);
    if (container) {
      const focusedPageElement = container.querySelector('.hasFocus');
      focusedPageElement && focusedPageElement.classList.remove('hasFocus');
    }
  }

  removeFocusWithinClass() {
    const container = document.querySelector(this.props.containerSelector);
    if (container) {
      const focusedPageElement = container.querySelector('.focusWithin');
      focusedPageElement && focusedPageElement.classList.remove('focusWithin');
    }
  }

  // allows focus class to be applied to elements only when focused via tab key
  // focus within class applied to element with focusableClass if it contains the active tabbed focused element
  addFocusClass() {
    const nextFocused = document.activeElement;
    const nextDisplayItem = findClosestAncestor(nextFocused, `.${this.props.focusableClass}`);
    const isNextDisplayItem = nextFocused.classList.contains(this.props.focusableClass);
    const container = document.querySelector(this.props.containerSelector);

    if (nextFocused.classList.contains('hasFocus')) return; // another UGC wall on page already moved focus class

    // remove focusWithin class if moving focus to next display item
    if (!!isNextDisplayItem) {
      this.removeFocusWithinClass();
    }

    // if moving focus on or within a display item add focusWithin
    if (!!nextDisplayItem && !nextDisplayItem.classList.contains('focusWithin')) {
      nextDisplayItem.classList.add('focusWithin');
    }

    if (container.contains(nextFocused)) {
      nextFocused.classList.add('hasFocus');
    }
  }

  render() {
    return this.props.children;
  }
}

TabbedBrowsing.propTypes = {
  focusableClass:    PropTypes.string,
  containerSelector: PropTypes.string.isRequired,
  onEnter:           PropTypes.func,
};

TabbedBrowsing.defaultProps = {
  focusableClass: 'xIsFocusable',
  onEnter:        _.noop,
};

export default TabbedBrowsing;
